// Shared, pure scene-timeline builder. Single source of truth for scene duration
// and audio/video track lists used by both live playback and the recorder.
//
// Lifted verbatim from _visor/containers/VisorApp.jsx _startRecorderInterval
// (the track + duration math). Pure function — no this, no side-effects.
//
// Track shape: { src, startSec, endSec, volume, trimStartSec?, id?, offsetSec? }
//   id        — the soundList entry's id (narration only); lets the scheduler pick
//               the right Howl when split pieces share one src.
//   offsetSec — seconds into the source file where the track starts (narration
//               audioOffset, set by a timeline split). Omitted when 0.
// Return shape: { durationSec, durationFrames, narration, pluginAudio, video }

import { audioCueManual } from "./audio_cue_manual.es6";

export const FPS = 30;

export function parseTimeStr(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v !== "string") return 0;
  const parts = v.split(":").map((x) => Number(x) || 0);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

// A narration's authored level as 0..1. The add-sound modals store
// volumeLevel (0..1); older entries carry volume (0..100).
export function narrationVolume(s) {
  if (!s) return 1;
  const v =
    typeof s.volumeLevel === "number"
      ? s.volumeLevel
      : typeof s.volume === "number"
        ? s.volume / 100
        : 1;
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 1;
}

// opts (all optional, defaults keep the pure/live behavior bit-identical):
//   fps            — frame->second divisor for every frame field (default 30 = FPS).
//                    The recorder passes window.__RECORDER_FPS, which can be != 30.
//   animEndSec     — extra end-time (seconds) folded into the duration max. The
//                    recorder derives this from its caller-built animationList; the
//                    live path has no animationList and passes 0.
//   minTimeFloorSec — when the content-derived duration is <= 0, floor it to this.
//                    Only applied when > 0, so the default (0) is a no-op and the
//                    live path returns the raw content duration unchanged.
export function buildSceneTimeline(sceneId, state, opts = {}) {
  const { fps = FPS, animEndSec = 0, minTimeFloorSec = 0 } = opts;

  if (!state || !sceneId) {
    return { durationSec: 0, durationFrames: 0, narration: [], pluginAudio: [], video: [] };
  }

  const navItem = (state.navItemsById || {})[sceneId] || {};
  const navItemDurSec =
    typeof navItem.duration === "number" && navItem.duration > 0 ? navItem.duration / fps : 0;

  const boxEndSec = Object.keys(state.boxesById || {}).reduce((m, key) => {
    const b = state.boxesById[key];
    if (b && b.parent === sceneId && b.timeline && typeof b.timeline.endFrame === "number") {
      return Math.max(m, b.timeline.endFrame / fps);
    }
    return m;
  }, 0);

  // Timeline eye toggle: hidden sounds neither play nor extend the scene.
  const soundList = ((state.soundList && state.soundList[sceneId]) || []).filter(
    (s) => s && s.visible !== false
  );
  // loopScene sounds tile across the whole scene (see the narration-tiling
  // block below) — their own endTime is just one tile's period, not a driver
  // of scene duration, so they're excluded from the duration-driving max.
  const loopSoundList = soundList.filter((s) => s.loopScene);
  const soundListEndSec = soundList
    .filter((s) => !s.loopScene)
    .reduce((m, s) => Math.max(m, parseTimeStr(s.endTime)), 0);

  // The authored scene duration (the editor's it-duration-marker, persisted as
  // navItem.duration) is AUTHORITATIVE once set: the scene ends exactly there —
  // it caps longer sounds/boxes instead of acting as a mere floor. Content only
  // derives the duration for scenes whose marker was never persisted.
  const hasAuthoredDuration = navItemDurSec > 0;
  let durationSec = hasAuthoredDuration
    ? navItemDurSec
    : Math.max(animEndSec, boxEndSec, soundListEndSec);
  // Edge case: a loop sound with nothing else to drive duration would
  // otherwise collapse the scene to less than one playthrough — fall back to
  // playing it once (include its own endTime) instead.
  if (!hasAuthoredDuration && loopSoundList.length > 0) {
    const loopStartMax = loopSoundList.reduce((m, s) => Math.max(m, parseTimeStr(s.startTime)), 0);
    if (durationSec <= loopStartMax) {
      const loopEndMax = loopSoundList.reduce((m, s) => Math.max(m, parseTimeStr(s.endTime)), 0);
      durationSec = Math.max(durationSec, loopEndMax);
    }
  }
  // Recorder-only floor: when content yields no duration, fall back to the scene's
  // minTime. Default minTimeFloorSec=0 keeps the live path floor-free (content-derived).
  if (durationSec <= 0 && minTimeFloorSec > 0) durationSec = minTimeFloorSec;

  const narration = [];
  soundList
    .filter((s) => s && s.file)
    .forEach((s) => {
      const startSec = parseTimeStr(s.startTime);
      const endSecRaw = parseTimeStr(s.endTime);
      const volume = narrationVolume(s);
      const offsetSec = Number(s.audioOffset) || 0;
      const extra = {
        ...(s.id ? { id: s.id } : {}),
        ...(offsetSec > 0 ? { offsetSec } : {}),
      };

      if (!s.loopScene) {
        const endSec = endSecRaw > startSec ? endSecRaw : durationSec;
        narration.push({ src: s.file, startSec, endSec, volume, ...extra });
        return;
      }

      // Tile across the rest of the scene: repeated tracks sharing the same
      // src, so AudioScheduler's per-src Howl lookup restarts the same Howl
      // at each tile boundary — no scheduler/Howl changes needed.
      const period = endSecRaw > startSec ? endSecRaw - startSec : parseTimeStr(s.duration);
      if (!(period > 0)) return;
      for (let tileStart = startSec; tileStart < durationSec; tileStart += period) {
        narration.push({
          src: s.file,
          startSec: tileStart,
          endSec: Math.min(tileStart + period, durationSec),
          volume,
          ...extra,
        });
      }
    });

  const pluginAudio = [];
  const video = [];

  Object.keys(state.boxesById || {}).forEach((boxId) => {
    const box = state.boxesById[boxId];
    if (!box || box.parent !== sceneId) return;
    const tb = (state.pluginToolbarsById || {})[boxId];
    if (!tb) return;
    const tl = box.timeline || {};
    // Hidden boxes (timeline eye toggle) don't play or gate the scene.
    if ((tl.visible !== undefined ? tl.visible : box.visible) === false) return;
    const startFrame = typeof tl.startFrame === "number" ? tl.startFrame : 0;
    const endFrame = typeof tl.endFrame === "number" ? tl.endFrame : 0;

    if (tb.pluginId === "AudioCue" || tb.pluginId === "EnrichedAudio") {
      const url = tb.state && tb.state.url;
      if (!url) return;
      // Nobody presses a manual cue in an export.
      if (tb.pluginId === "AudioCue" && audioCueManual(tb.state, box)) return;
      const startSec = startFrame / fps;
      let endSec = endFrame > startFrame ? endFrame / fps : durationSec;
      // Split/trimmed audio boxes carry the same timeline.trimStart/trimEnd a
      // video split writes: trimStartSec is the in-point inside the source file
      // (the mixer's offsetSec), and the play window never outlasts the trim.
      const trimStart = typeof tl.trimStart === "number" ? tl.trimStart : 0;
      const trimEnd = typeof tl.trimEnd === "number" ? tl.trimEnd : null;
      if (trimEnd != null && trimEnd > trimStart) {
        endSec = Math.min(endSec, startSec + (trimEnd - trimStart) / fps);
      }
      const parsedVolume = parseFloat(tb.state.volume);
      const volume = Number.isFinite(parsedVolume) ? parsedVolume / 100 : 1;
      pluginAudio.push({
        src: url,
        startSec,
        endSec,
        volume,
        ...(trimStart > 0 ? { trimStartSec: trimStart / fps } : {}),
      });
    } else if (tb.pluginId === "EnrichedPlayer" || tb.pluginId === "AvatarPlayer") {
      // AvatarPlayer keeps its clip under `videoUrl` (its `url` stays free for
      // the poster). Miss this and an animated avatar renders perfectly in the
      // editor and the visor, then exports as a silent mime.
      const url =
        tb.pluginId === "AvatarPlayer"
          ? tb.state && tb.state.jobStatus === "done" && tb.state.videoUrl
          : tb.state && tb.state.url;
      if (!url) return;
      const trimStart = typeof tl.trimStart === "number" ? tl.trimStart : 0;
      const trimEnd = typeof tl.trimEnd === "number" ? tl.trimEnd : null;
      // autoPlay videos play their window starting at playStartFrame (may be
      // after the clip start); manual videos play from the clip start.
      //
      // An AvatarPlayer is always timeline-driven — a presenter is not a video
      // the learner operates — so it defaults to autoPlay when the box predates
      // that field. Boxes generated before the plugin carried `autoPlay` would
      // otherwise be treated as manual videos the timeline never starts.
      const autoPlay =
        tb.pluginId === "AvatarPlayer"
          ? tb.state && tb.state.autoPlay !== undefined
            ? !!tb.state.autoPlay
            : true
          : !!(tb.state && tb.state.autoPlay);
      const playStartFrame =
        autoPlay && typeof tl.playStartFrame === "number" ? tl.playStartFrame : startFrame;
      const startSec = playStartFrame / fps;
      const trimmedDurSec =
        trimEnd != null ? (trimEnd - trimStart) / fps : (endFrame - startFrame) / fps;
      const endSec = startSec + Math.max(0, trimmedDurSec);
      if (endSec - startSec <= 0) return;
      // EnrichedPlayer persists the author's volume as 0..1 (0 = muted).
      const volume = typeof tb.state.volume === "number" ? tb.state.volume : 1;
      video.push({
        boxId,
        src: url,
        startSec,
        endSec,
        volume,
        trimStartSec: trimStart / fps,
      });
    }
  });

  // A video extends the scene: playing for N seconds means the scene lasts at
  // least N seconds even when the box has no explicit timeline.endFrame (the
  // author never touched it on the timeline). Without this the scene duration
  // collapses to the min-time floor and a waitForVideo/autoNext scene advances
  // early instead of waiting for the video. An AUTHORED duration wins here too:
  // the marker cuts the scene even under a longer video.
  const videoEndSec = video.reduce((m, v) => Math.max(m, v.endSec), 0);
  if (!hasAuthoredDuration && videoEndSec > durationSec) durationSec = videoEndSec;

  // An authored duration cuts audio exactly at the scene end: clamp every
  // track window so the scheduler stops (and never starts) sounds past it —
  // matching how images/videos leave the stage when the scene ends. The
  // recorder manifest inherits the same cut.
  const clampTracks = (list) =>
    list
      .filter((t) => t.startSec < durationSec)
      .map((t) => (t.endSec > durationSec ? { ...t, endSec: durationSec } : t));
  const narrationOut = hasAuthoredDuration ? clampTracks(narration) : narration;
  const pluginAudioOut = hasAuthoredDuration ? clampTracks(pluginAudio) : pluginAudio;
  const videoOut = hasAuthoredDuration ? clampTracks(video) : video;

  const durationFrames = Math.round(durationSec * fps);
  return {
    durationSec,
    durationFrames,
    narration: narrationOut,
    pluginAudio: pluginAudioOut,
    video: videoOut,
  };
}

export function getSceneDurationSec(sceneId, state) {
  return buildSceneTimeline(sceneId, state).durationSec;
}
